import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { toast } from 'sonner';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from '@tiptap/markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  List, ListOrdered, Quote, Heading1, Heading2, Heading3,
  Link2, Image as ImageIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, Palette,
  Undo2, Redo2,
} from 'lucide-react';

/**
 * Markdown-storage WYSIWYG editor (TipTap). Stores GitHub-flavoured
 * Markdown with inline HTML for the bits Markdown can't represent
 * (colours, font sizes, alignment, image attrs) — so the on-disk value
 * stays human-readable and degrades gracefully for the LLM-based
 * suggestion engine and any future plain-text fallback.
 *
 * Props mirror the shadcn <Textarea> so this is a drop-in replacement
 * everywhere RequirementsFormPage uses long-text fields:
 *   value, onChange (passes Markdown string), placeholder,
 *   rows (used as min-height hint), className, data-testid, disabled.
 */

const SWATCHES = [
  '#0F2040', '#4ABFCF', '#3B82F6', '#8B5CF6', '#14B8A6',
  '#10B981', '#F59E0B', '#EF4444', '#6B7280', '#000000',
];

function ToolbarButton({ onClick, active, disabled, title, testId, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="w-px h-5 bg-slate-200 mx-0.5" aria-hidden />;
}

function ColorPicker({ editor }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <ToolbarButton
        onClick={() => setOpen(o => !o)}
        active={open}
        title="Text colour"
        testId="rte-color"
      >
        <Palette className="w-3.5 h-3.5" />
      </ToolbarButton>
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 bg-white border border-slate-200 rounded-md shadow-lg p-2 flex gap-1 flex-wrap w-[140px]"
          data-testid="rte-color-popover"
          onMouseLeave={() => setOpen(false)}
        >
          {SWATCHES.map(c => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { editor.chain().focus().setColor(c).run(); setOpen(false); }}
              className="w-5 h-5 rounded border border-slate-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              title={c}
              data-testid={`rte-color-swatch-${c}`}
            />
          ))}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { editor.chain().focus().unsetColor().run(); setOpen(false); }}
            className="text-[10px] text-slate-500 underline hover:text-[#0F2040] w-full mt-1"
            data-testid="rte-color-clear"
          >
            Clear colour
          </button>
        </div>
      )}
    </div>
  );
}

function FontSizeSelect({ editor }) {
  const sizes = [
    { label: 'Small', value: '12px' },
    { label: 'Normal', value: '' },
    { label: 'Large', value: '18px' },
    { label: 'XL', value: '24px' },
  ];
  return (
    <select
      onChange={(e) => {
        const v = e.target.value;
        if (!v) editor.chain().focus().unsetMark('textStyle').run();
        else editor.chain().focus().setMark('textStyle', { fontSize: v }).run();
      }}
      className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white hover:bg-slate-50"
      data-testid="rte-font-size"
      defaultValue=""
    >
      <option value="" disabled>Size</option>
      {sizes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
}

// Custom TextStyle that supports fontSize attribute (TipTap's core
// TextStyle doesn't render fontSize by itself).
const TextStyleWithSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (el) => el.style.fontSize || null,
        renderHTML: (attrs) => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
      },
    };
  },
});

// ── Smart paste cleanup ────────────────────────────────────────────
// Word and Google Docs both pump enormous amounts of presentational
// HTML into the clipboard — Word wraps content in `<o:p>`, MS conditional
// comments, and `class="MsoListParagraph"` rather than real list nodes;
// Google Docs wraps everything in a no-op `<b style="font-weight:normal">`
// outer tag and litters spans with `font-family:Arial`, `color:#000000`,
// etc. Without normalisation, TipTap's default schema either drops the
// nodes (bullets become flat paragraphs) or keeps the styles (font
// inconsistency with the rest of the doc).
//
// The function below runs synchronously on every paste before TipTap's
// HTML→ProseMirror conversion. It is intentionally conservative:
// we only strip noise we can identify with high confidence, and we
// preserve every bit of *user-meaningful* structure (bold, italic,
// headings, real bullets, links). Anything we can't classify is left
// alone — so legitimate rich HTML continues to round-trip.
// Cheap pre-check used both by the cleanup helper (fast-path exit) and
// by the editor's paste wrapper to decide whether to surface a toast.
function _pasteLooksOfficey(html) {
  if (!html || typeof html !== 'string') return false;
  return /<\w+:|class=("|')?Mso|font-family|mso-/i.test(html)
    || /<b\s+[^>]*font-weight:\s*normal/i.test(html);
}

function smartCleanPastedHtml(html) {
  if (!html || typeof html !== 'string') return html || '';
  // Quick exit when there's nothing Office-y in the markup
  if (!_pasteLooksOfficey(html)) {
    return html;
  }
  let s = html;

  // 1. Drop Word/IE conditional comments (`<!--[if gte mso 9]>...<![endif]-->`).
  s = s.replace(/<!--\[if[\s\S]*?endif\]-->/gi, '');
  // 2. Drop <style>, <link>, <meta>, <title>, <head>, xml.
  s = s.replace(/<(?:style|script|link|meta|title|head|xml)[\s\S]*?>[\s\S]*?<\/(?:style|script|link|meta|title|head|xml)>/gi, '');
  s = s.replace(/<(?:style|script|link|meta|title|head|xml)[^>]*\/?>/gi, '');
  // 3. Drop Office namespace tags entirely (<o:p>, <w:foo>, <v:bar>, etc.).
  s = s.replace(/<\/?[a-z]:[a-z0-9-]+[^>]*>/gi, '');
  // 4. Google Docs wraps copied content in <b style="font-weight:normal;...">
  //    so the whole fragment ends up bold-tagged but visually unbold.
  //    Unwrap that outer <b> when its font-weight is explicitly normal.
  s = s.replace(/<b(\s+[^>]*font-weight:\s*normal[^>]*)>([\s\S]*?)<\/b>/gi, '$2');

  // The remaining transforms benefit from a real DOM walk.
  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__paste_root">${s}</div>`, 'text/html');
  } catch {
    return s;
  }
  const root = doc.getElementById('__paste_root');
  if (!root) return s;

  // 5. Word lists: paragraphs flagged with `class="MsoListParagraph*"`
  //    or `style="mso-list:..."` are really list items. Group consecutive
  //    runs of these into <ul>/<ol> based on a leading bullet/number
  //    character if present, falling back to <ul>.
  const isWordListItem = (el) => {
    if (el.tagName !== 'P') return false;
    const cls = (el.getAttribute('class') || '').toLowerCase();
    const styl = (el.getAttribute('style') || '').toLowerCase();
    return cls.includes('msolist') || styl.includes('mso-list');
  };
  const stripLeadingMarker = (el) => {
    // Word puts the bullet/number itself inside the first <span> of the
    // paragraph, often followed by &nbsp; / tab. Remove that visual
    // marker so the real <ul>/<ol> can render its own.
    const firstSpan = el.querySelector('span');
    if (firstSpan) {
      const t = firstSpan.textContent || '';
      // Strip leading number-with-dot, bullet glyphs, or single letters
      // followed by ')' that Word uses for ordered lists.
      const cleaned = t.replace(/^[\s\u00A0]*(?:[\u2022\u00B7\u25CF\u25E6\u204D\u2043o*-]|\(?\d+[).]|[a-zA-Z][).])[\s\u00A0\t]+/, '');
      if (cleaned !== t) firstSpan.textContent = cleaned;
    }
  };
  const detectOrdered = (el) => {
    // Try to detect ordered-vs-unordered by inspecting the first span's
    // leading marker.
    const firstSpan = el.querySelector('span');
    const t = (firstSpan?.textContent || '').trim();
    return /^\(?\d+[).]/.test(t) || /^[a-zA-Z][).]/.test(t);
  };
  const all = Array.from(root.querySelectorAll('p'));
  let i = 0;
  while (i < all.length) {
    if (!isWordListItem(all[i])) { i++; continue; }
    const ordered = detectOrdered(all[i]);
    const listTag = ordered ? 'ol' : 'ul';
    const list = doc.createElement(listTag);
    all[i].parentNode.insertBefore(list, all[i]);
    while (i < all.length && isWordListItem(all[i])) {
      const p = all[i];
      stripLeadingMarker(p);
      const li = doc.createElement('li');
      while (p.firstChild) li.appendChild(p.firstChild);
      list.appendChild(li);
      p.remove();
      i++;
    }
  }

  // 6. Sweep through every element and strip noise attributes.
  //    Keep: href (a), src/alt (img), colspan/rowspan (table), data-*.
  //    Drop everything else (style, class, lang, dir, align, ...). This
  //    is aggressive on purpose — anything semantic should come through
  //    as a real tag (<b>, <i>, <a>, <ul>, <li>) not as inline styling.
  const KEEP_ATTRS = new Set(['href', 'src', 'alt', 'colspan', 'rowspan', 'title']);
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (KEEP_ATTRS.has(name) || name.startsWith('data-')) continue;
      el.removeAttribute(attr.name);
    }
  });

  // 7. Unwrap empty/redundant spans, <font>, and Office-flavoured divs
  //    that no longer carry any attribute (and so contribute nothing).
  const unwrapTags = new Set(['SPAN', 'FONT', 'O', 'DIV']);
  let pass = 0;
  while (pass < 4) {
    let changed = false;
    root.querySelectorAll('*').forEach((el) => {
      if (!unwrapTags.has(el.tagName)) return;
      if (el.tagName === 'DIV') {
        // Only unwrap <div> if it's a single-child wrapper with no attrs
        if (el.attributes.length) return;
      }
      if (el.attributes.length) return;
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      changed = true;
    });
    if (!changed) break;
    pass++;
  }

  // 8. Collapse runs of &nbsp; and stray whitespace artefacts.
  root.querySelectorAll('p, li, td, th').forEach((el) => {
    el.innerHTML = el.innerHTML
      .replace(/(&nbsp;|\u00A0){2,}/g, ' ')
      .replace(/^(\s|&nbsp;|\u00A0)+|(\s|&nbsp;|\u00A0)+$/g, '');
  });
  // Drop empty paragraphs Word loves to emit between sections.
  root.querySelectorAll('p').forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector('img, br')) el.remove();
  });

  return root.innerHTML;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  className = '',
  disabled = false,
  compact = false,
  onMultiFileDrop,  // v260.104 — optional bulk-import hook
  onUploadImage,    // optional async (File) => url — when set, the image button uploads a file instead of asking for a URL
  'data-testid': testId,
}) {
  const minHeight = useMemo(() => `${Math.max(rows * 20, 80)}px`, [rows]);
  // Compact mode keeps the editor instance mounted at all times (so
  // pastes always flow through smartCleanPastedHtml / the toast) but
  // hides the toolbar and clamps the surface height until the user
  // focuses the field. Slack/Gmail composer pattern.
  const [isFocused, setIsFocused] = useState(false);
  // v260.136 — `forceEdit` lets a click on the collapsed Markdown
  // preview switch the field back into edit mode. The previous design
  // rendered the preview INSTEAD OF <EditorContent>, so the editor's
  // ProseMirror view was unmounted while collapsed and a preview click
  // calling `editor.commands.focus()` silently no-op'd — making any
  // compact field effectively read-only once it had saved content.
  const [forceEdit, setForceEdit] = useState(false);
  const expanded = !compact || isFocused || forceEdit;
  // Stashes the raw HTML of the most recent dirty paste so the
  // "Show original" toast action can swap the cleaned content back
  // to the un-touched original via a one-shot undo+insert.
  const lastDirtyPasteRef = useRef(null);
  const editorRef = useRef(null);
  // v260.102 — Drag-and-drop file → markdown import state. Declared
  // BEFORE the `if (!editor) return ...` guard so the hook order
  // stays stable across renders (Rules of Hooks).
  // v260.103 — `dragShift` mirrors the Shift modifier during the
  // drag so the overlay can hint at "replace vs insert" before the
  // user commits to the drop. The actual replace/insert decision is
  // taken from `e.shiftKey` on the drop event, not from this state
  // (avoids races if the OS misses a Shift up/down notification).
  const [dragActive, setDragActive] = useState(false);
  const [dragShift, setDragShift] = useState(false);
  const [importing, setImporting] = useState(false);
  const dragDepth = useRef(0);  // dragenter/leave counter

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Markdown extension brings its own paragraph/heading/list handling
        // but works on top of StarterKit nodes; default settings are fine.
        // StarterKit v3 bundles Link + Underline so we configure them here
        // instead of importing the standalone extensions (which would
        // collide and emit "duplicate extension names" warnings).
        link: { openOnClick: false, HTMLAttributes: { class: 'text-[#4ABFCF] underline' } },
        codeBlock: { HTMLAttributes: { class: 'bg-slate-100 rounded p-2 text-xs font-mono' } },
        blockquote: { HTMLAttributes: { class: 'border-l-2 border-slate-300 pl-3 text-slate-600' } },
        // v260.116 — Explicit Tailwind classes restore list bullets/numbers
        // inside the editor. Without these, Tailwind's preflight (Modern
        // Normalize) strips `list-style` from <ul>/<ol> and our project
        // doesn't ship the `@tailwindcss/typography` plugin, so the
        // `prose` wrapper does nothing for list markers.
        bulletList: { HTMLAttributes: { class: 'list-disc pl-6 my-2' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal pl-6 my-2' } },
        listItem: { HTMLAttributes: { class: 'my-0.5' } },
      }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'max-w-full rounded' } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'border-collapse w-full text-sm my-2' } }),
      TableRow,
      TableHeader.configure({ HTMLAttributes: { class: 'border border-slate-300 bg-slate-50 px-2 py-1 text-left font-semibold' } }),
      TableCell.configure({ HTMLAttributes: { class: 'border border-slate-300 px-2 py-1' } }),
      TextStyleWithSize,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || 'Start typing…' }),
      Markdown.configure({
        // v260.101 — Switched from the unmaintained `tiptap-markdown`
        // 0.9.0 to the official `@tiptap/markdown` 3.23.6. The old
        // package had known serialization bugs in TipTap v3 around
        // bullet/ordered lists and paragraph blank-line round-trips
        // (Word-pasted bullets vanishing on save, Enter-key paragraph
        // breaks not surviving reload).
        // The new package auto-handles those — only knob we need is
        // the indentation style for nested lists.
        indentation: { style: 'space', size: 2 },
      }),
    ],
    // Tell TipTap the initial `content` is markdown (not HTML) so
    // round-trip parsing uses the markdown parser. Without this, an
    // initial value of `- foo\n- bar` is parsed as plain text and the
    // bullets are baked into a single paragraph.
    contentType: 'markdown',
    content: reflowMarkdown(value || ''),
    editable: !disabled,
    editorProps: {
      transformPastedHTML: (html) => {
        const wasOfficey = _pasteLooksOfficey(html);
        const cleaned = smartCleanPastedHtml(html);
        if (wasOfficey && cleaned !== html) {
          // Stash the raw HTML so the toast's "Show original" action can
          // swap it back. Cleared by the action itself or on the next
          // paste (so the ref always reflects the most recent dirty paste).
          lastDirtyPasteRef.current = html;
          // Subtle confirmation so users who just pasted a 30-line spec
          // know their bullets/headings came through clean rather than
          // sitting underneath a wall of Calibri-11 inline styles.
          // Auto-dismisses in 5s; deduped by toast id so a multi-paste
          // doesn't stack the same banner. 5s gives users time to read
          // the action label and decide whether to click "Show original".
          toast.success('Paste cleaned · formatting preserved', {
            description: 'Removed Word / Google Docs styling so it matches the rest of your doc.',
            id: 'rte-paste-cleaned',
            duration: 5000,
            action: {
              label: 'Show original',
              onClick: () => {
                const raw = lastDirtyPasteRef.current;
                const ed = editorRef.current;
                if (!raw || !ed) return;
                // Undo the cleaned paste, then insert the raw HTML in
                // its place. `insertContent` accepts HTML strings — the
                // editor parses them through ProseMirror's HTML import
                // (which respects inline styles, so colours/fonts that
                // we previously stripped will now render).
                ed.chain().focus().undo().insertContent(raw, {
                  parseOptions: { preserveWhitespace: 'full' },
                }).run();
                lastDirtyPasteRef.current = null;
              },
            },
          });
        }
        return cleaned;
      },
    },
    onUpdate: ({ editor: ed }) => {
      try {
        // v260.101 — `@tiptap/markdown` 3.23.6 attaches `getMarkdown`
        // directly to the editor instance (the old `tiptap-markdown`
        // 0.9.0 exposed it via `storage.markdown.getMarkdown`).
        const md = ed.getMarkdown ? ed.getMarkdown() : ed.storage.markdown?.getMarkdown?.();
        onChange?.(md ?? ed.getHTML());
      } catch {
        onChange?.(ed.getHTML());
      }
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => { setIsFocused(false); setForceEdit(false); },
  });

  // v260.136 — When the user clicks the collapsed preview we mount the
  // editor (via forceEdit) and then focus it on the next frame, once the
  // ProseMirror DOM view actually exists.
  useEffect(() => {
    if (forceEdit && editor) {
      const raf = requestAnimationFrame(() => {
        try { editor.commands.focus('end'); } catch { /* view not ready */ }
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [forceEdit, editor]);

  // Sync external value changes (e.g. transcript import, undo from parent)
  // without clobbering the user's cursor on every keystroke.
  useEffect(() => {
    if (!editor) return;
    editorRef.current = editor;
    const current = (() => {
      // v260.101 — Use the new direct editor.getMarkdown() with a
      // graceful fallback for any defensive path.
      try {
        return editor.getMarkdown ? editor.getMarkdown() : editor.storage.markdown?.getMarkdown?.();
      } catch { return editor.getHTML(); }
    })();
    if ((value || '') !== (current || '')) {
      // @tiptap/markdown 3.23.6 overrides setContent: markdown parsing
      // only runs when `options.contentType === 'markdown'`. The old v2
      // signature (`setContent(value, false, {parseOptions})`) silently
      // skipped parsing, so imported markdown rendered as raw text on
      // load. v3 signature: setContent(content, { contentType, emitUpdate }).
      // reflowMarkdown re-structures AI-imported text whose newlines were
      // flattened so headings/lists survive the round-trip.
      const incoming = reflowMarkdown(value || '');
      if (incoming !== (current || '')) {
        editor.commands.setContent(incoming, {
          contentType: 'markdown',
          emitUpdate: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div
        className={`border border-slate-200 rounded-md ${className}`}
        style={{ minHeight }}
        data-testid={testId}
      />
    );
  }

  const promptLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const promptImage = () => {
    if (onUploadImage) {
      const input = document.createElement('input');
      input.type = 'file'; input.accept = 'image/png,image/jpeg,image/gif,image/webp';
      input.onchange = async () => {
        const f = input.files?.[0]; if (!f) return;
        try { const url = await onUploadImage(f); if (url) editor.chain().focus().setImage({ src: url, alt: f.name.replace(/\.[^.]+$/, '') }).run(); } catch { /* caller toasts */ }
      };
      input.click();
      return;
    }
    const url = window.prompt('Image URL');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  // v260.102 — Drag-and-drop file → markdown import handlers. The
  // hook state itself is declared earlier in this component (before
  // the `if (!editor) return ...` guard) to honour the Rules of
  // Hooks. These callbacks are recreated each render but only
  // touched in the rare case where the user actually drags a file —
  // not worth memoising.
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };
  const onDragOver = (e) => {
    if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      // v260.103 — Sync the overlay's "replace vs insert" hint with
      // the live Shift state. `dragover` fires continuously so we
      // pick up Shift presses/releases that happen mid-drag.
      if (e.shiftKey !== dragShift) setDragShift(e.shiftKey);
    }
  };
  const onDrop = async (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    setDragShift(false);
    const fileList = e.dataTransfer.files || [];
    // v260.104 — Multi-file drops are routed to the bulk-import
    // wizard (if a host registered one). Single-file drops keep the
    // original at-cursor / Shift-to-replace single-field flow.
    if (fileList.length > 1 && typeof onMultiFileDrop === 'function') {
      onMultiFileDrop(fileList);
      return;
    }
    const file = fileList[0];
    if (!file) return;
    const lower = (file.name || '').toLowerCase();
    const isMd = lower.endsWith('.md') || lower.endsWith('.markdown') || lower.endsWith('.txt');
    const isDocx = lower.endsWith('.docx');
    if (!isMd && !isDocx) {
      toast.error(`Unsupported file type — drop .md, .txt, or .docx`);
      return;
    }
    // v260.103 — Snapshot Shift state from the drop event itself so
    // there's no race with `setDragShift` re-renders.
    const replaceMode = !!e.shiftKey;
    setImporting(true);
    try {
      let markdown;
      if (isMd) {
        markdown = await file.text();
        markdown = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/files/to-markdown`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `Server returned ${r.status}`);
        }
        const json = await r.json();
        markdown = json.markdown || '';
      }
      if (editor && markdown) {
        if (replaceMode) {
          // REPLACE — `setContent` swaps the whole document so the
          // user can use a fresh spec doc as a clean baseline.
          editor.commands.setContent(markdown, true, {
            parseOptions: { contentType: 'markdown' },
          });
        } else {
          // INSERT — drop at cursor so users can layer multiple
          // sections into the same field.
          editor.chain().focus().insertContent(markdown, {
            parseOptions: { contentType: 'markdown' },
          }).run();
        }
      }
      toast.success(`${replaceMode ? 'Replaced from' : 'Imported'} ${file.name}`);
      // First-use hint — only fire once per browser so we don't
      // nag returning users. Stored under a versioned key so we
      // can re-introduce the hint if the convention changes.
      try {
        const hintKey = 'rte-shift-drop-hint-v1';
        if (!replaceMode && !window.localStorage.getItem(hintKey)) {
          toast.info('Tip: hold ⇧ Shift while dropping to REPLACE the whole field.', { duration: 6000 });
          window.localStorage.setItem(hintKey, '1');
        }
      } catch {
        // localStorage disabled — fine, just skip the hint.
      }
    } catch (err) {
      console.error('file-import failed:', err);
      toast.error(`Couldn't import ${file.name}: ${err.message || err}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className={`relative border border-slate-200 rounded-md bg-white focus-within:border-[#4ABFCF] focus-within:ring-1 focus-within:ring-[#4ABFCF]/30 transition-colors ${className}`}
      data-testid={testId}
      data-expanded={expanded ? 'true' : 'false'}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* v260.102 — Drag-drop overlay. Appears only while a file is
          being dragged over the editor; clicking through (pointer-
          events:none) keeps the underlying editor interactive on
          spurious dragenters. */}
      {(dragActive || importing) && (
        <div
          className={`absolute inset-0 z-20 flex items-center justify-center pointer-events-none rounded-md border-2 border-dashed ${
            dragShift
              ? 'bg-rose-50/40 border-rose-400'
              : 'bg-[#4ABFCF]/10 border-[#4ABFCF]'
          }`}
          data-testid="rte-drop-overlay"
          data-mode={dragShift ? 'replace' : 'insert'}
        >
          <div className={`bg-white shadow-md rounded-md px-3 py-1.5 text-xs font-medium border ${
            dragShift ? 'border-rose-300 text-rose-700' : 'border-[#4ABFCF]/40 text-[#0F2040]'
          }`}>
            {importing
              ? 'Importing…'
              : (dragShift
                  ? 'Release to REPLACE the field'
                  : <>Drop to insert · hold <kbd className="px-1 py-px rounded border border-slate-300 bg-slate-50 text-[10px] font-mono">⇧ Shift</kbd> to replace</>
                )
            }
          </div>
        </div>
      )}
      {/* Toolbar — kept mounted but visually hidden in compact+collapsed
          mode so the editor always remains alive (pastes still flow
          through smartCleanPastedHtml). */}
      <div
        className={`flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 ${expanded ? '' : 'hidden'}`}
        data-testid="rte-toolbar"
      >
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="rte-undo"><Undo2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="rte-redo"><Redo2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1" testId="rte-h1"><Heading1 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" testId="rte-h2"><Heading2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3" testId="rte-h3"><Heading3 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" testId="rte-bold"><Bold className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" testId="rte-italic"><Italic className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline" testId="rte-underline"><UnderlineIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testId="rte-strike"><Strikethrough className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code" testId="rte-code"><Code className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list" testId="rte-bullet"><List className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list" testId="rte-ordered"><ListOrdered className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote" testId="rte-quote"><Quote className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left" testId="rte-align-left"><AlignLeft className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align centre" testId="rte-align-center"><AlignCenter className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right" testId="rte-align-right"><AlignRight className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarDivider />
        <ColorPicker editor={editor} />
        <FontSizeSelect editor={editor} />
        <ToolbarDivider />
        <ToolbarButton onClick={promptLink} active={editor.isActive('link')} title="Insert link" testId="rte-link"><Link2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={promptImage} title="Insert image" testId="rte-image"><ImageIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={insertTable} title="Insert table" testId="rte-table"><TableIcon className="w-3.5 h-3.5" /></ToolbarButton>
      </div>
      {/* Editor surface — height clamped in compact+collapsed mode so
          the field looks like a single-line preview row, but the
          underlying ProseMirror is still mounted and accepting input
          (including pastes that route through smartCleanPastedHtml).
          When the field is compact + collapsed + has content, render
          a formatted Markdown preview instead of the raw editor view
          so the bullet list shows bullets, headings show bold, etc.
          The preview div is click-to-focus so the editor takes over
          the moment the user wants to interact. */}
      {compact && !isFocused && !forceEdit && value && value.trim() ? (
        <div
          className="group/preview relative px-3 py-1.5 cursor-text rounded-b-md hover:bg-slate-50/60 transition-colors"
          onClick={() => setForceEdit(true)}
          title="Click to edit"
          data-testid="rte-compact-preview-wrap"
        >
          {/* v260.136 — Collapsed preview now renders the saved Markdown
              with real formatting (bullets, numbered lists, paragraph /
              line breaks, headings, bold, links) and clamps height
              instead of flattening everything onto one truncated line.
              Previously the inline+truncate styling made formatted /
              imported content look like an unformatted "big text block"
              and read as non-editable. */}
          <div
            className="prose prose-sm max-w-none text-xs text-slate-700 max-h-40 overflow-hidden [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:my-0.5 [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:my-1 [&_h2]:my-1 [&_h3]:my-1 [&_strong]:font-semibold [&_a]:text-[#4ABFCF] [&_a]:underline"
            data-testid="rte-compact-preview"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {reflowMarkdown(value)}
            </ReactMarkdown>
          </div>
          <span
            className="pointer-events-none absolute top-1 right-2 opacity-0 group-hover/preview:opacity-100 transition-opacity text-[9px] text-slate-400 bg-white/85 px-1 rounded inline-flex items-center gap-0.5"
            data-testid="rte-compact-edit-hint"
          >
            click to edit
          </span>
        </div>
      ) : (
        <EditorContent
          editor={editor}
          className={`prose prose-sm max-w-none px-3 py-2 focus:outline-none transition-[max-height] duration-150 [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[var(--rte-min-h)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 ${
            expanded
              ? ''
              : 'max-h-[36px] overflow-hidden [&_.ProseMirror]:!min-h-[20px] [&_.ProseMirror_*]:!my-0 [&_.ProseMirror_p]:truncate'
          }`}
          style={{ '--rte-min-h': expanded ? minHeight : '20px' }}
        />
      )}
    </div>
  );
}

/**
 * One-time best-effort plain-text → Markdown coercion, applied at load
 * time for legacy records. Conservative: only converts patterns that
 * are unambiguous so we never mangle valid Markdown or rich content.
 *
 *  • Lines starting with "- ", "• ", "* ", "› " → bullet list items
 *  • Lines starting with "1. ", "2) " etc          → already Markdown
 *  • Double newlines preserved as paragraph breaks
 *
 * Returns the same input unchanged if it already looks like Markdown
 * (contains any of: ##, **, _italic_, ```, or html tags) so we don't
 * double-format already-rich content.
 */
/**
 * Conservative markdown reflow for AI-imported text whose newlines were
 * collapsed to spaces (so headings/lists/labels render as a wall of text).
 * Re-inserts block structure around unambiguous markers. Non-destructive
 * and idempotent on already well-structured markdown.
 *
 *  • `##`..`######` heading runs → own line, normalised spacing
 *  • `**Label:**` bold field-labels sitting mid-line → own paragraph
 *  • inline " - item" bullet runs → one list item per line
 */
export function reflowMarkdown(src) {
  if (!src || typeof src !== 'string') return src || '';
  let s = src.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Headings: break before 2-6 '#' runs followed by a letter/digit
  // (avoids "#1"/"C#" false positives) and normalise the trailing space.
  s = s.replace(/\s*(#{2,6})\s*(?=[A-Za-z0-9])/g, '\n\n$1 ');
  // Bold field-labels ("**Direction:**") currently mid-line → own paragraph.
  s = s.replace(/([^\n])[ \t]+(\*\*[^*\n]{1,60}:\*\*)/g, '$1\n\n$2');
  // Inline " - item" bullets → one per line ("Flat-file" hyphens untouched).
  s = s.replace(/([^\n])[ \t]+-[ \t]+(?=\S)/g, '$1\n- ');
  // Tidy whitespace.
  s = s.replace(/\n{3,}/g, '\n\n').replace(/^\n+/, '').trimEnd();
  return s;
}

export function plainTextToMarkdown(text) {
  if (!text || typeof text !== 'string') return text || '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  // Already-rich heuristic
  if (/(^|\n)(#{1,6} )|(\*\*|__)[^*_\n]+(\*\*|__)|```|<\/?[a-z]+/i.test(text)) return text;
  const lines = text.split(/\r?\n/);
  const out = [];
  for (const raw of lines) {
    const line = raw.replace(/[ \t]+$/, '');
    const m = line.match(/^[ \t]*([\u2022\u2023\u2043\u204C\u204D›▪◦●*-])\s+(.*)$/);
    if (m) {
      out.push(`- ${m[2]}`);
    } else {
      out.push(line);
    }
  }
  return out.join('\n');
}
